import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { paymentActions } from '../../_actions';
import toast from '../shared/Toast';

/**
 * Stripe / payouts status, derived on the front end.
 *
 * `payment/getBank` already returns the whole Stripe Connect account
 * (charges_enabled, payouts_enabled, details_submitted, requirements,
 * external_accounts, settings.payouts.schedule) plus Beu's own approval
 * flag `merchantStatus` ('pending' | 'flagged' | anything else = approved).
 * Nothing here needs a backend change. An owner should never have to sign
 * in to Stripe to find out whether they can take money and get paid.
 *
 * Overall keys, in precedence order:
 *   beu_review   Beu has not approved the account yet
 *   not_started  no Stripe account / nothing submitted
 *   action_needed Stripe needs information (past or currently due, or disabled)
 *   verifying    Stripe is checking what was sent
 *   restricted   details in, but charges or payouts switched off with nothing to do
 *   active       charges and payouts both enabled
 *   unknown      we could not load it
 */

const REQ_LABELS = [
  [/^individual\.verification\.additional_document/, 'Proof of address for the account owner'],
  [/^individual\.verification\.document/, 'Photo ID for the account owner'],
  [/^company\.verification\.document/, 'A document verifying the company'],
  [/^individual\.id_number/, 'National Insurance number for the account owner'],
  [/^individual\.dob/, 'Date of birth for the account owner'],
  [/^individual\.address/, 'Home address for the account owner'],
  [/^individual\.(first_name|last_name)/, 'Full name of the account owner'],
  [/^individual\.(phone|email)/, 'Contact details for the account owner'],
  [/^company\.tax_id/, 'Company registration number'],
  [/^company\.address/, 'Registered company address'],
  [/^company\.name/, 'Registered company name'],
  [/^company\.(owners|directors|executives)_provided/, 'Details of the company owners or directors'],
  [/^(representative|owners|directors|persons?)\./, 'Details and ID for the people on the account'],
  [/^person_/, 'Details and ID for the people on the account'],
  [/^business_profile\.(url|product_description)/, 'Your website or a short description of the business'],
  [/^business_profile\.mcc/, 'Your type of business'],
  [/^business_profile\.support_phone/, 'A phone number clients can reach you on'],
  [/^external_account/, 'A bank account for payouts'],
  [/^tos_acceptance/, 'Accepting Stripe\'s terms of service'],
  [/^settings\.payments\.statement_descriptor/, 'The name that appears on card statements'],
  [/^business_type/, 'Whether you trade as an individual or a company'],
];

export function humaniseRequirement(code) {
  const c = String(code || '');
  for (const [re, label] of REQ_LABELS) if (re.test(c)) return label;
  return c.replace(/[._]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

const DISABLED_LABELS = {
  'requirements.past_due': 'Some information is overdue',
  'requirements.pending_verification': 'Stripe is verifying your details',
  under_review: 'Stripe is reviewing the account',
  listed: 'The account has been restricted by Stripe',
  'rejected.fraud': 'Stripe has closed the account',
  'rejected.listed': 'Stripe has closed the account',
  'rejected.terms_of_service': 'Stripe has closed the account',
  'rejected.other': 'Stripe has closed the account',
  other: 'The account has been restricted',
  platform_paused: 'Payouts are paused',
};

const uniq = (arr) => Array.from(new Set(arr));

const fmtDeadline = (unix) => {
  if (!unix) return '';
  try { return new Date(unix * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch (e) { return ''; }
};

/**
 * Pure. Returns { key, tone, label, chip, note, needs, cta, bank }.
 *   key   not_connected | beu_review | issue | verifying | restricted | active | unknown
 *   tone  'ok' | 'warn' | 'bad' | 'off'
 *   label the one line; chip a short warning (or null); note one sentence under it
 */
export function deriveStripeStatus({ account, merchantStatus, error } = {}) {
  const a = account && typeof account === 'object' ? account : null;
  const hasAccount = !!(a && (a.id || a.details_submitted !== undefined || a.charges_enabled !== undefined));
  const req = (a && a.requirements) || {};
  const pastDue = req.past_due || [];
  const currentlyDue = req.currently_due || [];
  const pendingVer = req.pending_verification || [];
  const disabled = req.disabled_reason || null;
  const ext = (a && a.external_accounts && a.external_accounts.data) || [];
  const bankRaw = ext.find((x) => x.default_for_currency) || ext[0] || null;
  const bank = bankRaw ? { name: bankRaw.bank_name || 'your bank', last4: bankRaw.last4 || '', status: bankRaw.status || '' } : null;
  const beuPending = merchantStatus === 'pending' || merchantStatus === 'flagged';
  const chargesOn = !!(a && a.charges_enabled);
  const payoutsOn = !!(a && a.payouts_enabled);
  const submitted = !!(a && a.details_submitted);
  // One line per thing to do: Stripe lists a code per person and per field
  // (ten people's details, the terms' date and IP), which read as repeats
  const needs = uniq(pastDue.concat(currentlyDue).map(humaniseRequirement));
  const deadline = fmtDeadline(req.current_deadline);
  const PAUSE_NOTE = 'You may be unable to take payments or withdraw until this is fixed.';

  const notFound = error && /not found|no (stripe )?account|not connected|no bank|not set ?up/i.test(String(error));
  const nothingYet = !hasAccount || (!submitted && !bank && !currentlyDue.length && !pastDue.length);
  if (error && !hasAccount && !notFound && !beuPending) {
    return { key: 'unknown', tone: 'off', label: 'Could not check your Stripe account', chip: null, note: 'Try again in a minute.', needs: [], cta: 'Try again', bank: null };
  }
  if (nothingYet && beuPending) {
    // New salon: Beu approval comes before they can add a Stripe account.
    return { key: 'beu_review', tone: 'warn', label: 'No Stripe account connected', chip: 'Awaiting Beu approval', note: 'You can add a Stripe account once Beu has approved you, usually within one working day.', needs: [], cta: null, bank: null };
  }
  if (nothingYet) {
    return { key: 'not_connected', tone: 'off', label: 'No Stripe account connected', chip: null, note: 'Add a Stripe account to accept payments through Beu.', needs: [], cta: 'Add Stripe account', bank: null };
  }
  if (beuPending) {
    return { key: 'beu_review', tone: 'warn', label: 'Stripe account connected', chip: merchantStatus === 'flagged' ? 'Being re-checked' : 'Awaiting Beu approval', note: 'You may be unable to take payments or withdraw until Beu has approved your account, usually within one working day.', needs: [], cta: null, bank };
  }
  if (disabled && /rejected|listed/.test(disabled) && !(pastDue.length || currentlyDue.length)) {
    return { key: 'restricted', tone: 'bad', label: 'Stripe account connected', chip: /rejected/.test(disabled) ? 'Closed by Stripe' : 'Restricted by Stripe', note: 'You cannot take payments or withdraw. Contact support and we will take it up with Stripe.', needs: [], cta: null, bank };
  }
  if (pastDue.length || currentlyDue.length || (disabled && !/pending_verification|under_review/.test(disabled)) || (bank && /errored|verification_failed/.test(bank.status))) {
    const bankBad = bank && /errored|verification_failed/.test(bank.status);
    const list = bankBad && !needs.length ? ['A working bank account for payouts'] : needs;
    return { key: 'issue', tone: pastDue.length || !chargesOn || !payoutsOn ? 'bad' : 'warn', label: 'Stripe account connected', chip: 'Needs attention', note: PAUSE_NOTE, needs: list, deadline, cta: 'Fix on Stripe', bank };
  }
  if (pendingVer.length || disabled === 'requirements.pending_verification' || disabled === 'under_review') {
    return { key: 'verifying', tone: 'warn', label: 'Stripe account connected', chip: 'Verifying', note: 'Stripe is checking your details. You may be unable to withdraw until this is done, usually within a day.', needs: [], cta: null, bank };
  }
  if (chargesOn && payoutsOn) {
    return { key: 'active', tone: 'ok', label: 'Stripe account connected', chip: null, note: bank ? `Payouts go to ${bank.name} ending ${bank.last4}.` : '', needs: [], cta: null, bank };
  }
  return { key: 'restricted', tone: 'bad', label: 'Stripe account connected', chip: !chargesOn ? 'Payments paused' : 'Payouts paused', note: `${DISABLED_LABELS[disabled] ? DISABLED_LABELS[disabled] + '. ' : ''}${PAUSE_NOTE} Contact support if this does not clear.`, needs: [], cta: null, bank };
}

/* ---------- hook: one fetch per session, shared across settings pages ---------- */

let cache = { merchantStatus: undefined, error: null, loaded: false };
let inflight = null;
const subscribers = new Set();
const notify = () => subscribers.forEach((fn) => { try { fn((n) => n + 1); } catch (e) { /* unmounted */ } });

export function refreshStripeStatus() { cache = { merchantStatus: undefined, error: null, loaded: false }; inflight = null; notify(); }

/**
 * Reads the Stripe account from redux (bankDetails) and fetches it once if
 * nobody has. Every mounted subscriber re-renders when the fetch lands, not
 * only the one that started it. Pass `own` when the page already loaded the
 * data, to avoid a second request: { account, merchantStatus, error, loading }.
 */
export function useStripeStatus(own, enabled = true) {
  const dispatch = useDispatch();
  const stored = useSelector((s) => s.bankDetails);
  const [, tick] = useState(0);
  const skip = !!own || !enabled;

  useEffect(() => {
    subscribers.add(tick);
    if (skip) return () => { subscribers.delete(tick); };
    if (!cache.loaded && !inflight) {
      const done = () => { inflight = null; notify(); };
      // Promise.resolve: the action returns a third-party promise without .finally
      inflight = Promise.resolve(dispatch(paymentActions.getBank({}))).then(
        (res) => { cache = { merchantStatus: (res && res.data && res.data.responseData && res.data.responseData.merchantStatus) || null, error: null, loaded: true }; done(); },
        (err) => {
          const msg = (err && err.response && err.response.data && (err.response.data.error?.responseMessage || err.response.data.message)) || (err && err.message) || 'error';
          cache = { merchantStatus: null, error: msg, loaded: true }; done();
        },
      );
    }
    return () => { subscribers.delete(tick); };
  }, [dispatch, skip]);

  const account = own ? own.account : stored;
  const merchantStatus = own ? own.merchantStatus : cache.merchantStatus;
  const error = own ? own.error : cache.error;
  const loading = own ? !!own.loading : !cache.loaded;
  const demo = enabled ? getDemo() : '';
  const sample = demo ? demoSample() : null;
  if (sample) return { ...deriveStripeStatus(sample), loading: false, demo };
  if (!enabled) return { ...deriveStripeStatus({}), key: 'unknown', loading: true, demo: '' };
  return { ...deriveStripeStatus({ account, merchantStatus, error }), loading, demo: '' };
}

/* ---------- the persistent alert: who sees it, and the one action ---------- */

// Same list the side nav uses: pages a logged-in owner can be on that are
// not the app (their own booking page, policies, sign-in).
const PUBLIC_PATH_PREFIXES = ['/login', '/signup', '/forgot-password', '/reset/', '/social-login', '/redirect', '/forms/fill/', '/book/', '/salon/', '/pro/', '/store/', '/gift-cards/', '/hair-salons/', '/barbers/', '/beauty-salons/', '/nail-salons/', '/spas/', '/terms', '/privacy', '/partner-terms', '/beu-pay-terms', '/hairdresser-terms-and-conditions', '/kiosk', '/sales-x7k9', '/join', '/magic', '/get-software', '/hairdressers-home'];
export const isPublicPath = (pathname) => pathname === '/' || PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));

// Mirrors BANK_CHANGES_DISABLED on the Payouts page: flip to halt bank changes in an incident.
export const STRIPE_LINK_DISABLED = false;

/**
 * "Fix now": open Stripe's hosted onboarding for this account (the same
 * bank-link the Payouts page uses). Falls back to the Payouts page with the
 * reason on screen if Stripe or Beu blocks it.
 */
export function openStripeFix(dispatch, history, status) {
  if (status && status.demo) { toast.success('Demo: this would open Stripe with the account ready to fix.'); return Promise.resolve(); }
  if (status && status.key === 'restricted') { if (history) history.push('/settings/payout-account'); return Promise.resolve(); }
  if (STRIPE_LINK_DISABLED) { toast.error('Changing your Stripe details is temporarily unavailable. Please contact support.'); return Promise.resolve(); }
  return Promise.resolve(dispatch(paymentActions.bankLink({}))).then((response) => {
    const url = response && response.data && response.data.statusCode === 1 && response.data.responseData && response.data.responseData.result && response.data.responseData.result.url;
    if (url) { window.open(url, '_self'); return; }
    const msg = (response && response.data && (response.data.error?.responseMessage || response.data.message)) || 'We could not open Stripe just now. Please try again.';
    toast.error(msg);
    if (history) history.push('/settings/payout-account');
  }).catch((err) => {
    const msg = (err && err.response && err.response.data && (err.response.data.error?.responseMessage || err.response.data.message)) || 'We could not open Stripe just now. Please try again.';
    toast.error(msg);
    if (history) history.push('/settings/payout-account');
  });
}

/**
 * The persistent alert. Active only for a logged-in owner or self-employed
 * stylist, on app pages, when Stripe needs something or has paused the
 * account. Employed staff and clients never trigger the fetch.
 */
export function useStripeAlert() {
  const user = useSelector((s) => s.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const h = user && user.userInfo && user.userInfo.hairdresser;
  const isPro = !!(h && (h.isSalonOwner === 1 || h.employmentTypeId === 2));
  let signedIn = false;
  try { signedIn = !!localStorage.getItem('accessToken'); } catch (e) { signedIn = false; }
  const enabled = isPro && signedIn && !isPublicPath(location.pathname);
  const status = useStripeStatus(undefined, enabled);
  const active = enabled && !status.loading && (status.key === 'issue' || status.key === 'restricted');
  const fix = () => openStripeFix(dispatch, history, status);
  return { active, status, fix };
}

/* ---------- demo states (dev, staging, beta; never production) ----------
   Click any status chip to step through the states so the backend team can
   see each screen without a broken Stripe account. Kept in sessionStorage so
   every surface (pages, hub pill, bell, persistent bar) shows the same thing.
   ?stripePreview=<key> in the address seeds it. */
const DEMO_KEY = 'beu-stripe-demo';
export const DEMO_ORDER = ['live', 'issue', 'paused', 'restricted', 'not_connected', 'beu_review', 'verifying'];
export const DEMO_LABEL = { live: 'Connected', issue: 'Needs attention', paused: 'Paused (overdue)', restricted: 'Closed by Stripe', not_connected: 'Not connected', beu_review: 'Awaiting Beu approval', verifying: 'Verifying' };
export const demoAllowed = () => !['prod', 'production', 'rush', 'rush_prod'].includes(String(process.env.REACT_APP_ENV || '').toLowerCase());

export function getDemo() {
  if (!demoAllowed() || typeof window === 'undefined') return '';
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('stripePreview');
    if (fromUrl && DEMO_ORDER.includes(fromUrl)) { sessionStorage.setItem(DEMO_KEY, fromUrl === 'live' ? '' : fromUrl); }
    const k = sessionStorage.getItem(DEMO_KEY) || '';
    return DEMO_ORDER.includes(k) && k !== 'live' ? k : '';
  } catch (e) { return ''; }
}
export function setDemo(k) {
  if (!demoAllowed()) return;
  try { sessionStorage.setItem(DEMO_KEY, k && k !== 'live' ? k : ''); } catch (e) { /* ignore */ }
  try { // drop the URL seed so it does not override the click
    const u = new URL(window.location.href);
    if (u.searchParams.has('stripePreview')) { u.searchParams.delete('stripePreview'); window.history.replaceState(null, '', u.toString()); }
  } catch (e) { /* ignore */ }
  notify();
}
export function cycleDemo() {
  const cur = getDemo() || 'live';
  const next = DEMO_ORDER[(DEMO_ORDER.indexOf(cur) + 1) % DEMO_ORDER.length];
  setDemo(next);
}

function demoSample() {
  const k = getDemo();
  if (!k) return null;
  const base = { id: 'acct_demo', details_submitted: true, charges_enabled: true, payouts_enabled: true, requirements: { currently_due: [], past_due: [], pending_verification: [], disabled_reason: null }, external_accounts: { data: [{ bank_name: 'Monzo', last4: '2345', status: 'new', default_for_currency: true }] } };
  const samples = {
    not_connected: { account: {}, merchantStatus: null },
    beu_review: { account: {}, merchantStatus: 'pending' },
    issue: { account: { ...base, requirements: { currently_due: ['individual.verification.document', 'business_profile.url'], past_due: [], pending_verification: [], disabled_reason: null, current_deadline: Math.floor(Date.now() / 1000) + 14 * 86400 } }, merchantStatus: null },
    paused: { account: { ...base, charges_enabled: false, payouts_enabled: false, requirements: { currently_due: ['external_account'], past_due: ['individual.verification.additional_document'], pending_verification: [], disabled_reason: 'requirements.past_due' } }, merchantStatus: null },
    verifying: { account: { ...base, payouts_enabled: false, requirements: { currently_due: [], past_due: [], pending_verification: ['individual.verification.document'], disabled_reason: 'requirements.pending_verification' } }, merchantStatus: null },
    restricted: { account: { ...base, charges_enabled: false, payouts_enabled: false, requirements: { currently_due: [], past_due: [], pending_verification: [], disabled_reason: 'rejected.fraud' } }, merchantStatus: null },
  };
  return samples[k] || null;
}
